import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Modal, Form, Offcanvas } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';
import Swal from 'sweetalert2';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${backendUrl}/api/entrytypes`;

const EntryTypeList = () => {
    const [entryTypes, setEntryTypes] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        label: '',
        name: '',
        description: ''
    });
    const [isEdit, setIsEdit] = useState(false);
    const [selectedType, setSelectedType] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const debounceTimeoutRef = useRef(null);

    useEffect(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            fetchEntryTypes();
        }, searchQuery ? 500 : 0);

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const fetchEntryTypes = async () => {
        setLoading(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
            const response = await axios.get(API_URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                }
            });
            let data = response.data || [];
            if (searchQuery) {
                data = data.filter(item => 
                    (item.name || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
                    (item.label || '').toLowerCase().includes(searchQuery.toLowerCase())
                );
            }
            setEntryTypes(data);
        } catch (error) {
            console.error('Error fetching entry types:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddType = () => {
        setFormData({
            label: '',
            name: '',
            description: ''
        });
        setIsEdit(false);
        setSelectedType(null);
        setShowModal(true);
        setErrors({});
    };

    const handleEditType = (type) => {
        setFormData({
            label: type.label || '',
            name: type.name || '',
            description: type.description || ''
        });
        setIsEdit(true);
        setSelectedType(type);
        setShowModal(true);
        setErrors({});
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const userInfo = JSON.parse(localStorage.getItem('user-info'));
                    const origin = localStorage.getItem('user-origin');
                    const token = userInfo?.token;
                    await axios.delete(`${API_URL}/${id}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Tenant-Origin': origin,
                        },
                    });
                    fetchEntryTypes();
                    Swal.fire("Deleted!", "Entry type has been deleted.", "success");
                } catch (error) {
                    console.error('Error deleting entry type:', error);
                    Swal.fire("Error!", "Failed to delete entry type.", "error");
                }
            }
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: "",
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name?.trim()) newErrors.name = 'Name is required';
        if (!formData.label?.trim()) newErrors.label = 'Label is required';
        return newErrors;
    };

    const handleSubmit = async () => {
        const userInfo = JSON.parse(localStorage.getItem('user-info'));
        const origin = localStorage.getItem('user-origin');
        const token = userInfo?.token;
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        try {
            if (isEdit) {
                await axios.patch(`${API_URL}/${selectedType.id}`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                });
                Swal.fire("Updated!", "Entry type has been updated.", "success");
            } else {
                await axios.post(API_URL, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                });
                Swal.fire("Saved!", "Entry type has been created.", "success");
            }
            setShowModal(false);
            fetchEntryTypes();
        } catch (error) {
            console.error('Error saving entry type:', error);
            Swal.fire("Error!", "Failed to save entry type.", "error");
        }
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid ">
                    <div className="row w-100">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Entry Types Setup</h4>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-3">
                                        <div className="d-flex">
                                            <div className="search-box me-2 mb-2 d-inline-block">
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Search..."
                                                        value={searchQuery}
                                                        onChange={handleSearchChange}
                                                    />
                                                    <i className="bx bx-search-alt search-icon" />
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                                            onClick={handleAddType}
                                            data-bs-toggle="tooltip"
                                            data-bs-placement="top"
                                            title='Add Entry Type'
                                        >
                                            <i className="mdi mdi-plus"></i>
                                        </button>
                                    </div>

                                    <table className="table align-middle table-nowrap table-check table-list">
                                        <thead className="table-sales-gradient">
                                            <tr>
                                                <th style={{ width: '20px' }} className="align-middle">#</th>
                                                <th className="align-middle">Name</th>
                                                <th className="align-middle">Label</th>
                                                <th className="align-middle">Description</th>
                                                <th className="align-middle">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan="6" className="text-center">Loading...</td>
                                                </tr>
                                            ) : entryTypes.length > 0 ? (
                                                entryTypes.map((type, index) => (
                                                    <tr key={type.id}>
                                                        <td>{index + 1}</td>
                                                        <td>{type.name}</td>
                                                        <td>{type.label}</td>
                                                        <td>{type.description}</td>
                                                        <td>
                                                            <div className="dropdown">
                                                                <i
                                                                    className="mdi mdi-dots-horizontal font-size-18"
                                                                    data-bs-toggle="dropdown"
                                                                    aria-expanded="false"
                                                                    style={{ cursor: 'pointer' }}
                                                                ></i>
                                                                <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                                                    <li>
                                                                        <button className="dropdown-item" onClick={() => handleEditType(type)}>
                                                                            Edit
                                                                        </button>
                                                                    </li>
                                                                    <li>
                                                                        <button className="dropdown-item" onClick={() => handleDelete(type.id)}>
                                                                            Delete
                                                                        </button>
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="text-center">No records found.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Offcanvas show={showModal} onHide={() => setShowModal(false)} placement='end'>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>{isEdit ? 'Edit Entry Type' : 'Add Entry Type'}</Offcanvas.Title>
                </Offcanvas.Header>
                <hr style={{ margin: 0 }} /><br />
                <Offcanvas.Body>
                    <Form>
                        <Form.Group className='mb-3'>
                            <Form.Label style={{ fontSize: "13px" }}>Label *</Form.Label>
                            <Form.Control
                                type="text"
                                style={{ fontSize: "13px" }}
                                name="label"
                                value={formData.label}
                                onChange={handleInputChange}
                                className={errors.label ? 'form-control is-invalid' : 'form-control'}
                                placeholder="e.g. receipt"
                            />
                            {errors.label && <div className="invalid-feedback">*{errors.label}</div>}
                        </Form.Group>

                        <Form.Group className='mb-3'>
                            <Form.Label style={{ fontSize: "13px" }}>Name *</Form.Label>
                            <Form.Control
                                type="text"
                                style={{ fontSize: "13px" }}
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className={errors.name ? 'form-control is-invalid' : 'form-control'}
                                placeholder="e.g. Receipt"
                            />
                            {errors.name && <div className="invalid-feedback">*{errors.name}</div>}
                        </Form.Group>

                        <Form.Group className='mb-3'>
                            <Form.Label style={{ fontSize: "13px" }}>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                style={{ fontSize: "13px" }}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                className="form-control"
                            />
                        </Form.Group>
                    </Form>
                </Offcanvas.Body>
                <div className="p-3 d-flex justify-content-end border-top">
                    <Button variant="secondary me-2" onClick={() => setShowModal(false)}>Close</Button>
                    <Button variant="primary" onClick={handleSubmit}>{isEdit ? 'Update' : 'Save'} </Button>
                </div>
            </Offcanvas>
        </div>
    );
};

export default EntryTypeList;
